(() => {
  if (customElements.get('product-recommendations')) return;

  class ProductRecommendations extends HTMLElement {
    constructor() {
      super();
      this.requestVersion = 0;
      this.loading = false;
      this.failed = false;
      this.loaded = false;
      this.onRetry = event => {
        const button = event.target.closest?.('[data-recommendations-retry]'); 
        if (!button || button.closest('product-recommendations') !== this) return;
        event.preventDefault();
        if (!button.hidden && !button.disabled && !this.loading) this.load();
      };
    }

    connectedCallback() {
      this.addEventListener('click', this.onRetry);
      this.managed = this.hasAttribute('data-recommendations-state');
      this.content = this.querySelector('[data-recommendations-content]');
      this.status = this.querySelector('[data-recommendations-status]');
      this.retry = this.querySelector('[data-recommendations-retry]');
      if (this.loaded || this.failed || ['ready', 'empty'].includes(this.dataset.recommendationsState)) return;
      if (!this.managed && this.productLinks(this).length) { this.loaded = true; return; }
      if (typeof IntersectionObserver !== 'function') { this.load(); return; }
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.target === this && entry.isIntersecting)) this.load();
      }, {rootMargin: '100px 0px 200px 0px'});
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onRetry);
      this.observer?.disconnect();
      this.observer = null;
      this.requestVersion++;
      this.controller?.abort();
      this.controller = null;
      if (this.loading) {
        this.loading = false;
        this.failed = false;
        this.setStatus('');
        if (this.retry) this.retry.hidden = true;
      }
      this.setBusy(false);
      this.retry?.removeAttribute('aria-disabled');
    }

    setStatus(message) {
      if (this.status && this.status.textContent !== (message || '')) this.status.textContent = message || '';
    }

    setBusy(busy) {
      const target = this.content || this;
      if (busy) target.setAttribute('aria-busy', 'true');
      else target.removeAttribute('aria-busy');
    }

    productLinks(root) {
      return [...root.querySelectorAll('a[href]')].filter(link => {
        if (link.closest('[hidden],[inert],[aria-hidden="true"]')) return false;
        try {
          const url = new URL(link.getAttribute('href'), document.baseURI);
          return url.origin === location.origin && /\/products\/[^/]+\/?$/.test(url.pathname);
        } catch { return false; }
      });
    }

    responseContainer(html, requestedURL) {
      const containers = [...html.querySelectorAll('product-recommendations')];
      return containers.find(container => {
        if (!container.dataset.url) return !this.managed && containers.length === 1;
        try {
          const responseURL = new URL(container.dataset.url, document.baseURI);
          return responseURL.origin === requestedURL.origin && responseURL.pathname === requestedURL.pathname &&
            ['section_id', 'product_id'].every(key => responseURL.searchParams.get(key) === requestedURL.searchParams.get(key)) &&
            (responseURL.searchParams.get('intent') || 'related') === (requestedURL.searchParams.get('intent') || 'related');
        } catch { return false; }
      });
    }

    initializeSlider() {
      try {
        const result = this.querySelector('swiper-component')?.load?.();
        result?.catch?.(() => {});
      } catch {
        // Product links remain available if the optional slider cannot initialize.
      }
    }

    focusResult() {
      const link = this.productLinks(this.content || this)[0];
      if (link) { link.focus(); return; }
      if (this.status) {
        this.status.setAttribute('tabindex', '-1');
        this.status.focus();
      }
    }

    async load() {
      if (this.loading || this.loaded || !this.isConnected) return;
      this.observer?.disconnect();
      this.observer = null;
      this.loading = true;
      this.failed = false;
      const version = ++this.requestVersion;
      const controller = new AbortController();
      this.controller = controller;
      const current = () => this.isConnected && this.requestVersion === version && this.controller === controller;
      this.setBusy(true);
      this.setStatus(this.dataset.loadingMessage);
      // Keep a focused retry button present until completion. Internal request
      // gating prevents duplicates without disabling it and losing focus.
      if (this.retry && !this.retry.hidden) this.retry.setAttribute('aria-disabled', 'true');
      try {
        if (!this.dataset.url || (this.managed && !this.content)) throw new Error('Missing recommendations configuration');
        const requestedURL = new URL(this.dataset.url, document.baseURI);
        const response = await fetch(this.dataset.url, {signal: controller.signal});
        if (!current()) return;
        if (!response.ok) throw new Error('Recommendations request failed');
        const text = await response.text();
        if (!current()) return;
        const html = new DOMParser().parseFromString(text, 'text/html');
        const incoming = this.responseContainer(html, requestedURL);
        if (!incoming) throw new Error('Unexpected recommendations response');
        const retryFocused = this.retry && document.activeElement === this.retry;
        if (this.managed) {
          const state = incoming.dataset.recommendationsState;
          const content = incoming.querySelector('[data-recommendations-content]');
          if (!['ready', 'empty'].includes(state) || !content || (state === 'ready' && !this.productLinks(content).length)) {
            throw new Error('Incomplete recommendations response');
          }
          this.content.innerHTML = state === 'ready' ? content.innerHTML : '';
          this.dataset.recommendationsState = state;
          this.setStatus(state === 'ready' ? this.dataset.loadedMessage : this.dataset.emptyMessage);
        } else {
          this.innerHTML = incoming.innerHTML.trim();
          this.classList.add('empty-hidden');
        }
        this.loaded = true;
        if (this.retry) this.retry.hidden = true;
        this.initializeSlider();
        if (retryFocused) this.focusResult();
      } catch {
        if (!current()) return;
        this.failed = true;
        this.setStatus(this.dataset.errorMessage);
        if (this.retry) this.retry.hidden = false;
      } finally {
        if (current()) {
          this.loading = false;
          this.controller = null;
          this.setBusy(false);
          this.retry?.removeAttribute('aria-disabled');
        }
      }
    }
  }

  customElements.define('product-recommendations', ProductRecommendations);
})();
